import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { fullWidthHeightImageStyles, styles } from "./CarDetailsLpDetection.styles";
import { RefObject, SyntheticEvent, useEffect, useRef, useState } from "react";
import { ZoomButton } from "../../../../../elements/ZoomButton";
import { ZoomConfig } from "./utils/zoomConfig";
import { calculateCoordinates } from "./utils/calculateCoordinates";
import { updateImageUrl } from "./utils/partnerImagesUtils";
import { useBlockOneFingerTouch } from "./utils/useBlockOneFingerTouch";
import { IZoomDataLayerEvents } from "../../../CarSubmission";
import { ICarDetailsTranslations } from "../CarDetails";

const carFallbackImage = require("../images/car-fallback.svg");
const MOBILE_ONE_FINGER_PAN_MAX_WIDTH = 1024;

export interface ILpPrediction {
  score: number;
  bbox: number[];
}

export interface ICarDetailsLpDetectionProps {
  carImage?: string;
  isLicensePlateDetection: boolean;
  zoomDataLayerEvents: IZoomDataLayerEvents;
  fetchLpPrediction: (carImageUrl: string) => Promise<ILpPrediction>;
  translations: ICarDetailsTranslations;
}

function CarDetailsLpDetection({
  carImage = "",
  isLicensePlateDetection,
  zoomDataLayerEvents,
  fetchLpPrediction,
  translations,
}: ICarDetailsLpDetectionProps) {
  const [px, setPx] = useState<number | null>(null);
  const [py, setPy] = useState<number | null>(null);
  const [isFallbackImage, setIsFallbackImage] = useState(false);
  const carImgRef = useRef<HTMLImageElement>(null);
  const carImageWrapperRef = useRef<ReactZoomPanPinchRef>(null);
  const { ref: imageBoxRef, didBlock: showTwoFingersHint } = useBlockOneFingerTouch<HTMLDivElement>(
    MOBILE_ONE_FINGER_PAN_MAX_WIDTH,
  );
  const [currentScale, setCurrentScale] = useState(0);
  const initialScale = ZoomConfig.zoomInitScale;
  const maxScale = ZoomConfig.zoomMaxScale;
  const zoomStep = ZoomConfig.zoomStep;
  const zoomAnimationTime = ZoomConfig.animation;
  const carImageUrl = carImage ? updateImageUrl(carImage) : carFallbackImage;

  const onImageError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = carFallbackImage;
    setIsFallbackImage(true);
  };

  const handleTransform = (e: ReactZoomPanPinchRef) => {
    setCurrentScale(e.instance.state.scale);
  };

  const handleZoomOut = () => {
    if (carImageWrapperRef.current) {
      const { zoomOut } = carImageWrapperRef.current;
      zoomOut(zoomStep, zoomAnimationTime);
    }
  };
  const generalZoom = () => {
    if (carImageWrapperRef.current) {
      const { zoomIn } = carImageWrapperRef.current;
      zoomIn(zoomStep, zoomAnimationTime);
    }
  };

  const handleZoomIn = async () => {
    if (px && py) {
      zoomToLicensePlate(px, py);
    } else {
      generalZoom();
    }
  };

  const zoomToLicensePlate = (x: number, y: number) => {
    if (carImageWrapperRef.current && x && y) {
      const { setTransform } = carImageWrapperRef.current;
      setCurrentScale(maxScale);
      setTransform(x, y, maxScale, zoomAnimationTime);
    }
  };

  useEffect(() => {
    let prediction: ILpPrediction;
    const runPredictionApi = async () => {
      try {
        prediction = await fetchLpPrediction(carImageUrl);
        if (!prediction.score) {
          zoomDataLayerEvents.aiZoomNotTriggeredEvent();
          return;
        }
        const [cx, cy] = calculateCoordinates(prediction.bbox, carImgRef as RefObject<HTMLImageElement>);
        setPx(cx);
        setPy(cy);
        zoomToLicensePlate(cx, cy);
        zoomDataLayerEvents.aiZoomTriggeredEvent();
      } catch (_error) {
        zoomDataLayerEvents.aiZoomNotTriggeredEvent();
      }
    };
    if (isLicensePlateDetection) {
      runPredictionApi();
    }
  }, [isLicensePlateDetection]);

  return (
    <div
      ref={imageBoxRef}
      className={styles.imageBox(isFallbackImage)}
      style={
        {
          "--width-for-desktop": `${ZoomConfig.windowWidth}px`,
          "--height-for-desktop": `${ZoomConfig.windowHeight}px`,
        } as React.CSSProperties
      }
    >
      <TransformWrapper
        centerOnInit={true}
        maxScale={maxScale}
        initialScale={initialScale}
        onTransform={(e) => handleTransform(e)}
        ref={carImageWrapperRef}
        disabled={!isLicensePlateDetection}
      >
        {() => (
          <>
            <TransformComponent wrapperStyle={fullWidthHeightImageStyles} contentStyle={fullWidthHeightImageStyles}>
              <img
                src={carImageUrl}
                alt={translations.partnerCarImageAlt}
                crossOrigin="anonymous"
                onError={onImageError}
                ref={carImgRef}
              />
            </TransformComponent>
            {isLicensePlateDetection && (
              <div className={styles.zoomButton()}>
                <ZoomButton
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  currentScale={currentScale}
                  zoomInLabel={translations.ariaLabelZoomInButton || ""}
                  zoomOutLabel={translations.ariaLabelZoomOutButton || ""}
                  {...ZoomConfig}
                />
              </div>
            )}
          </>
        )}
      </TransformWrapper>
      {translations.useTwoFingersHint && (
        <div className={styles.twoFingersHint(showTwoFingersHint)} role="status" aria-live="polite">
          {translations.useTwoFingersHint}
        </div>
      )}
    </div>
  );
}

export default CarDetailsLpDetection;
